import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { absUrl } from "@/lib/urls";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Instagram, Truck, ShieldCheck, Gift } from "lucide-react";

export default function Home() {
  const [content, setContent] = useState({});
  const [featured, setFeatured] = useState([]);
  const [novidades, setNovidades] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const bust = `?v=${Date.now()}`;
    api.get(`/content${bust}`).then(({ data }) => setContent(data || {})).catch(() => {});
    api.get("/products/featured").then(({ data }) => setFeatured(data || [])).catch(() => {});
    api.get("/products/new").then(({ data }) => setNovidades(data || [])).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data || [])).catch(() => {});
  }, []);

  const heroImg = absUrl(content.hero_image) || "https://images.unsplash.com/photo-1585960622850-ed33c41d6418?w=1600";

  const videoEmbed = (() => {
    const url = content.video_url;
    if (!url) return null;
    const igMatch = url.match(/instagram\.com\/(reel|p|tv)\/([^/?]+)/);
    if (igMatch) return { src: `https://www.instagram.com/${igMatch[1]}/${igMatch[2]}/embed`, vertical: true };
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return { src: `https://www.youtube.com/embed/${ytMatch[1]}`, vertical: false };
    return { src: url, vertical: false };
  })();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#F8F5EF]">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-10 items-center">
          <div className="lg:col-span-5 px-6 lg:px-10 py-16 lg:py-24 order-2 lg:order-1 fade-in-up">
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#C2A064] mb-6">{content.hero_eyebrow || "— Nova coleção"}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#49392F]">
              {content.hero_title || "Joias que contam histórias"}
            </h1>
            <p className="font-body text-[15px] leading-relaxed text-[#49392F]/80 mt-6 max-w-md">
              {content.hero_subtitle || "Peças delicadas, feitas para serem lembradas."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/catalogo" className="duo-btn duo-btn-primary" data-testid="hero-cta-shop">
                Comprar agora
              </Link>
              <Link to="/catalogo" className="duo-btn duo-btn-secondary" data-testid="hero-cta-collection">
                Conheça a coleção
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 relative">
            <div className="aspect-[4/5] lg:aspect-[5/6] w-full overflow-hidden">
              <img src={heroImg} alt="Duo Áurea" className="w-full h-full object-cover" />
            </div>
            <div className="hidden lg:block absolute bottom-8 left-8 bg-[#F8F5EF]/90 backdrop-blur-md px-6 py-4">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C2A064]">{content.hero_overlay_eyebrow || "Edição Áurea"}</div>
              <div className="font-display text-xl text-[#49392F] mt-1">{content.hero_overlay_title || "Delicadeza em cada detalhe"}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#C2A064] mb-3">Categorias</p>
            <h2 className="font-display text-3xl md:text-4xl text-[#49392F]">Encontre a sua joia</h2>
          </div>
          <Link to="/catalogo" className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#49392F] link-elegant">
            Ver todas <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {categories.slice(0, 6).map((c, idx) => (
            <Link
              key={c.id}
              to={`/catalogo?category=${encodeURIComponent(c.name)}`}
              className="group block"
              data-testid={`category-${c.slug}`}
            >
              <div className="aspect-square bg-[#E8DCCB] overflow-hidden">
                <img
                  src={absUrl(c.image) || `https://images.unsplash.com/photo-${["1611652022419-a9419f74343d","1655255114527-d0a834d9a774","1591209627278-9e5c15b4e5e3","1535632066927-ab7c9ab60908","1599643478518-a784e5dc4c8f","1588444837495-c6cfeb53f32d"][idx % 6]}?w=600`}
                  alt={c.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="mt-3 font-body text-[12px] tracking-[0.2em] uppercase text-[#49392F] text-center">{c.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="bg-[#E8DCCB]/40 py-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <p className="text-[11px] tracking-[0.4em] uppercase text-[#C2A064] mb-3">Destaques</p>
              <h2 className="font-display text-3xl md:text-4xl text-[#49392F]">Mais desejadas</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
              {featured.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* NOVIDADES */}
      {novidades.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-[11px] tracking-[0.4em] uppercase text-[#C2A064] mb-3">Novidades</p>
              <h2 className="font-display text-3xl md:text-4xl text-[#49392F]">Recém-chegadas</h2>
            </div>
            <Link to="/catalogo?sort=new" className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#49392F] link-elegant">
              Ver tudo <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
            {novidades.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {novidades.length === 0 && featured.length === 0 && (
        <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 text-center">
          <h2 className="font-display text-3xl text-[#49392F]">Catálogo chegando em breve</h2>
          <p className="mt-4 text-[#49392F]/70 max-w-xl mx-auto">
            Nossas peças estão sendo cadastradas com todo o cuidado. Volte em instantes para conhecer a coleção completa.
          </p>
        </section>
      )}

      {/* INSTITUCIONAL */}
      <section className="bg-[#49392F] text-[#F8F5EF] py-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="aspect-[4/5] overflow-hidden">
            <img src={`${absUrl(content.about_image) || "https://images.unsplash.com/photo-1686575131650-e02f84970212?w=1000"}${content.about_image && content.about_image.startsWith("/api/") ? `?v=${content._v || ""}` : ""}`} alt="Duo Áurea" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#C2A064] mb-4">Sobre a Duo Áurea</p>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">Delicadeza, sofisticação e afeto em cada peça</h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[#F8F5EF]/80">
              {content.about_text || "Nascemos da paixão por detalhes. Cada joia é escolhida pensando em você — para marcar lembranças e tornar momentos únicos ainda mais especiais."}
            </p>
            <Link to="/institucional#sobre" className="duo-btn duo-btn-gold mt-10">
              Conheça nossa história
            </Link>
          </div>
        </div>
      </section>

      {/* VIDEO */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-10">
          <p className="text-[11px] tracking-[0.4em] uppercase text-[#C2A064] mb-3">Nossa história</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#49392F]">{content.video_section_title || "Conheça a Duo Áurea"}</h2>
          <p className="mt-4 text-[#49392F]/70 max-w-lg mx-auto text-[14px]">{content.video_section_subtitle || "Um pouco da origem, da proposta e dos bastidores da marca."}</p>
        </div>
        <div
          className={`relative w-full mx-auto bg-[#E8DCCB] border border-[#C2A064]/30 ${videoEmbed?.vertical ? "max-w-sm aspect-[9/16]" : "max-w-3xl aspect-video"}`}
        >
          {videoEmbed ? (
            <iframe
              src={videoEmbed.src}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              scrolling="no"
              allow="encrypted-media; autoplay; clipboard-write; picture-in-picture; web-share"
              allowFullScreen
              title="Vídeo Duo Áurea"
              data-testid="institutional-video"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-center p-10 text-[#49392F]/70 text-sm">
              O vídeo institucional será adicionado em breve.
            </div>
          )}
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section className="bg-[#F8F5EF] py-20 border-t border-[#C2A064]/20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
          {[
            { icon: Truck, title: "Entrega para todo o Brasil", desc: "Enviamos pelos Correios ou transportadora com embalagem especial." },
            { icon: ShieldCheck, title: "Compra protegida", desc: "Seus dados protegidos e atendimento próximo via WhatsApp." },
            { icon: Gift, title: "Embalagem presenteável", desc: "Suas peças chegam prontas para presentear." },
          ].map((d, i) => (
            <div key={i}>
              <d.icon size={28} className="mx-auto text-[#C2A064]" />
              <h4 className="font-display text-xl text-[#49392F] mt-5">{d.title}</h4>
              <p className="text-[13px] text-[#49392F]/70 mt-3 leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INSTAGRAM CTA */}
      {content.instagram_url && (
        <section className="py-20 text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase text-[#C2A064] mb-3">Siga-nos</p>
          <h3 className="font-display text-3xl text-[#49392F]">@duoaureajoias</h3>
          <a
            href={content.instagram_url}
            target="_blank"
            rel="noreferrer"
            className="duo-btn duo-btn-secondary mt-6 inline-flex"
            data-testid="instagram-cta"
          >
            <Instagram size={16} /> Siga no Instagram
          </a>
        </section>
      )}
    </div>
  );
}
